import { useMemo } from 'react'

import type { ColDef } from 'ag-grid-community'
import { useIntl } from 'react-intl'

import type { ManifestPivotRow } from '@b-stock/order-process-api-client'

import {
  CategoryCell,
  ConditionCell,
  ExtMsrpCell,
  SubcategoryCell,
  TextCell,
} from './cellRenderers'
import { pivotTableKeyToField, type PivotTableKey } from './utils'

type PivotRow = Partial<ManifestPivotRow>

export const defaultPivotColDef: ColDef<PivotRow> = {
  cellRenderer: TextCell,
  resizable: false,
  sortable: false,
  suppressHeaderContextMenu: true,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  wrapHeaderText: false, // since we compute fixed table height
}

export const usePivotColumnDefs = ({
  currency,
}: { currency?: string } = {}) => {
  const { formatMessage } = useIntl()

  return useMemo(() => {
    const buildSubjectColumn = (
      pivotTableKey: PivotTableKey
    ): ColDef<PivotRow> => ({
      field: pivotTableKeyToField[pivotTableKey],
      headerName: formatMessage({
        id: `Listing.manifestPivotTable.pivotTableKey.${pivotTableKey}`,
      }),
      minWidth: 200,
    })

    const subjectColumns: Record<PivotTableKey, ColDef<PivotRow>> = {
      BRAND: buildSubjectColumn('BRAND'),
      CATEGORY: {
        ...buildSubjectColumn('CATEGORY'),
        cellRenderer: CategoryCell,
      },
      CONDITION: {
        ...buildSubjectColumn('CONDITION'),
        cellRenderer: ConditionCell,
      },
      MODEL: buildSubjectColumn('MODEL'),
      SELLER_CATEGORY: buildSubjectColumn('SELLER_CATEGORY'),
      SELLER_GRADE: buildSubjectColumn('SELLER_GRADE'),
      SUBCATEGORY: {
        ...buildSubjectColumn('SUBCATEGORY'),
        cellRenderer: SubcategoryCell,
      },
    }

    const quantityColumn: ColDef<PivotRow> = {
      field: 'sumQuantity',
      headerName: formatMessage({
        id: 'Listing.manifestPivotTable.columns.sumQuantity',
      }),
      width: 120,
    }

    const extMsrpColumn: ColDef<PivotRow> = {
      cellRenderer: ExtMsrpCell,
      cellRendererParams: { currency },
      field: 'sumQuantityTimesAttributesUnitRetail',
      headerName: formatMessage({
        id: 'Listing.manifestPivotTable.columns.sumQuantityTimesAttributesUnitRetail',
      }),
      width: 160,
    }

    return { extMsrpColumn, quantityColumn, subjectColumns }
  }, [currency, formatMessage])
}
